/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, CircularProgress } from '@mui/material';

import { AlignCenter } from '../../components/AlignCenter/AlignCenter';
import {
  HUMaterialTable,
  HUMaterialTableProps,
  iconCellStyles,
} from '../../components/HUMaterialTable/HUMaterialTable';
import { HUSpin } from '../../components/HUSpin/HUSpin';
import { ServiceIcon } from '../../components/ServiceIcon';
import { SimpleConfirmModal } from '../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleErrorModal } from '../../components/SimpleErrorModal/SimpleErrorModal';
import { Namespaces } from '../../i18n';
import { SUPlanApiService } from '../../services/SUPlanApiService';
import { useMiscellaneousStyles } from '../../styles/miscellaneous';
import { ApiClientError } from '../../types/api/ApiClientError';
import { AdminPlanCompute } from '../../types/su-plan/entities/AdminPlanCompute';
import { adminFetchPlans } from '../../utils/queryFns';
import {
  reactToAdminCreatePlanCompute,
  reactToAdminDeletePlanCompute,
  reactToAdminUpdatePlanCompute,
} from '../../utils/reactions';
import { useIsMounted } from '../../utils/useIsMounted';
import { useObjectState } from '../../utils/useObjectState';
import { CreatePlanComputeDialog } from './components/CreatePlanComputeDialog/CreatePlanComputeDialog';
import { UpdatePlanComputeDialog } from './components/UpdatePlanComputeDialog/UpdatePlanComputeDialog';

type ModalState = {
  deleteModal: { isOpen: true; plan: AdminPlanCompute } | { isOpen: false; plan?: undefined };
  updateModal: { isOpen: true; plan: AdminPlanCompute } | { isOpen: false; plan?: undefined };
  createModal: { isOpen: boolean };
  errorModal: { isOpen: boolean; error: string };
};

export function AdminManageComputePlansPage() {
  const [modalState, setModalState] = useObjectState<ModalState>({
    deleteModal: { isOpen: false, plan: undefined },
    createModal: { isOpen: false },
    updateModal: { isOpen: false, plan: undefined },
    errorModal: { isOpen: false, error: '' },
  });
  const { deleteModal, createModal, errorModal, updateModal } = modalState;
  const { t } = useTranslation([Namespaces.Admin]);
  const miscClasses = useMiscellaneousStyles();
  const isMounted = useIsMounted();

  const plansQuery = useQuery(adminFetchPlans.key, adminFetchPlans.fn, {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const [deletePlanMutation, { isLoading }] = useMutation(SUPlanApiService.deletePlanCompute, {
    onSuccess: () => {
      if (!isMounted.current) {
        return;
      }
      void reactToAdminDeletePlanCompute();
    },
    onError: (e: ApiClientError) => {
      setModalState({
        errorModal: { isOpen: true, error: e.response?.data.message ?? '' },
      });
    },
  });

  const computePlans = useMemo(() => plansQuery.data?.payload.computePlans ?? [], [plansQuery.data]);

  const isLoadingQueries = useMemo(() => plansQuery.status === 'loading', [plansQuery.status]);
  const isError = false;

  const getStatusIcon = useCallback(
    (status: boolean | undefined) =>
      status ? (
        <CheckCircleIcon className={miscClasses.successColor} />
      ) : (
        <CancelIcon className={miscClasses.failureColor} />
      ),
    [miscClasses.failureColor, miscClasses.successColor],
  );

  const onDeleteIconClick = useCallback(
    (e: any, rowData: AdminPlanCompute | AdminPlanCompute[]) => {
      const v = Array.isArray(rowData) ? rowData[0] : rowData;
      setModalState({ deleteModal: { isOpen: true, plan: v } });
    },
    [setModalState],
  );

  const handleDeleteComputePlan = async ({ plan: { id } }: { plan: AdminPlanCompute }) => {
    await deletePlanMutation({ planId: id });
  };

  const onAddIconClick = useCallback(() => {
    setModalState({ createModal: { isOpen: true } });
  }, [setModalState]);

  const onEditIconModal = useCallback(
    (e: any, rowData: AdminPlanCompute | AdminPlanCompute[]) => {
      const v = Array.isArray(rowData) ? rowData[0] : rowData;
      setModalState({ updateModal: { isOpen: true, plan: v } });
    },
    [setModalState],
  );

  const onPlanCreateUpdateSuccess = async ({ type }: { type: 'create' | 'update' }) => {
    try {
      if (type === 'create') {
        setModalState({
          createModal: { isOpen: false },
        });
        await reactToAdminCreatePlanCompute();
      } else {
        setModalState({
          updateModal: { isOpen: false, plan: undefined },
        });
        await reactToAdminUpdatePlanCompute();
      }
    } catch {
      // do nothing
    }
  };

  const getLabelKey = useCallback((key: string): string => {
    return `manage_plans.dialogs.labels.${key}`;
  }, []);

  const getActionLabelKey = useCallback((key: string): string => {
    return `manage_plans.dialogs.actions.${key}`;
  }, []);

  const getDisableReasonLabelKey = useCallback((key: string): string => {
    return `manage_plans.disabled_reasons.${key}`;
  }, []);

  const tableConfig = useMemo<HUMaterialTableProps<AdminPlanCompute>>(
    () => ({
      columns: [
        {
          title: t(getLabelKey('id')),
          field: 'id',
        },
        {
          title: t(getLabelKey('name')),
          field: 'name',
        },
        {
          title: t(getLabelKey('cpu')),
          field: 'cpu',
        },
        {
          title: t(getLabelKey('memory')),
          field: 'memory',
        },
        {
          title: t(getLabelKey('replica_min')),
          field: 'replicaMin',
        },
        {
          title: t(getLabelKey('replica_max')),
          field: 'replicaMax',
        },
        {
          title: t(getLabelKey('replica_default')),
          field: 'replicaDefault',
        },
        {
          title: t(getLabelKey('service')),
          render: rowData => rowData.serviceType && <ServiceIcon serviceType={rowData.serviceType} variant="minimal" />,
          cellStyle: iconCellStyles,
          headerStyle: iconCellStyles,
        },
        {
          title: t(getLabelKey('is_visible')),
          render: rowData => getStatusIcon(rowData.isVisible),
          cellStyle: iconCellStyles,
          headerStyle: iconCellStyles,
        },
        {
          title: t(getLabelKey('is_default')),
          render: rowData => getStatusIcon(rowData.isDefault),
          cellStyle: iconCellStyles,
          headerStyle: iconCellStyles,
        },
      ],
      data: computePlans,
      title: t('manage_plans.compute_plans.title'),
      actions: [
        {
          icon: () => <AddIcon />,
          tooltip: t(getActionLabelKey('add')),
          isFreeAction: true,
          onClick: onAddIconClick,
        },
        {
          icon: () => <EditIcon />,
          tooltip: t(getActionLabelKey('edit')),
          onClick: onEditIconModal,
        },
        rowData => ({
          icon: () => <DeleteIcon />,
          tooltip:
            rowData.configurations > 0
              ? t(getDisableReasonLabelKey('plan_already_in_use'))
              : t(getActionLabelKey('delete')),
          onClick: onDeleteIconClick,
          disabled: rowData.configurations > 0,
        }),
      ],
      options: {
        actionsColumnIndex: -1,
        draggable: false,
        sorting: false,
        search: true,
      },
    }),
    [
      computePlans,
      getActionLabelKey,
      getLabelKey,
      getStatusIcon,
      onAddIconClick,
      onDeleteIconClick,
      onEditIconModal,
      t,
      getDisableReasonLabelKey,
    ],
  );

  return (
    <div>
      {isLoadingQueries ? (
        <AlignCenter className={miscClasses.loaderContainer}>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : isError ? (
        <Alert severity="error">{t(`${Namespaces.Common}:errors.unexpected_error`)}</Alert>
      ) : (
        <HUSpin spinning={isLoading} delay={50}>
          <HUMaterialTable {...tableConfig} />
        </HUSpin>
      )}
      <CreatePlanComputeDialog
        open={createModal.isOpen}
        onCancel={() => {
          setModalState({
            createModal: { isOpen: false },
          });
        }}
        onSuccess={() => onPlanCreateUpdateSuccess({ type: 'create' })}
      />
      <UpdatePlanComputeDialog
        open={updateModal.isOpen}
        planToEdit={updateModal.plan}
        onCancel={() => {
          setModalState({
            updateModal: { isOpen: false, plan: undefined },
          });
        }}
        onSuccess={() => onPlanCreateUpdateSuccess({ type: 'update' })}
      />
      <SimpleConfirmModal
        open={deleteModal.isOpen}
        {...(deleteModal.isOpen
          ? {
              message: t('manage_plans.dialogs.confirmation.delete_plan', {
                planName: deleteModal.plan.name,
              }),
              onCancel: () =>
                setModalState({
                  deleteModal: { isOpen: false, plan: undefined },
                }),
              onConfirm: () => {
                void handleDeleteComputePlan({ plan: deleteModal.plan });
                setModalState({
                  deleteModal: { isOpen: false, plan: undefined },
                });
              },
            }
          : {
              onConfirm: () => {},
              onCancel: () => {},
              message: '',
            })}
      />
      <SimpleErrorModal
        open={errorModal.isOpen}
        message={errorModal.error}
        onOk={() => {
          setModalState({
            errorModal: { isOpen: false, error: '' },
          });
        }}
      />
    </div>
  );
}
